import type { Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import type { PaymentTypeCode } from '@unify/fhir-model/resources/episode-of-care';
import { validateTreatmentPlanDates } from '@unify/fhir-model/resources/treatment-plan';

import {
  validateRequiredTemplateFields,
  type QuestionnaireFillValidationResult,
} from '../questionnaireResponseFillValidation';
import {
  findQuestionnaireItemByLinkIdSuffix,
  validateTreatmentPlanDiagnosisCodesForPaymentType,
} from '../treatmentPlanFormHelpers';

export type ChartDocumentSignValidationResult = QuestionnaireFillValidationResult;

/**
 * Validate required Questionnaire items before signing or sending a chart document.
 * Draft saves skip this check.
 *
 * When sending to the client portal (`skipClientFields`), required-field and template Zod
 * checks are deferred to the client portal — the provider may send with empty fields.
 */
export function validateChartDocumentBeforeSign(params: {
  questionnaire: Questionnaire;
  response: Pick<QuestionnaireResponse, 'item'> | undefined;
  skipClientFields?: boolean;
  episodePaymentType?: PaymentTypeCode;
  /** When true, insurance episodes cannot use Z diagnosis codes (treatment plan documents). */
  validateTreatmentPlanDiagnosis?: boolean;
}): ChartDocumentSignValidationResult {
  if (!params.response) {
    return {
      success: false,
      message: 'Form is still loading. Please wait and try again.',
      missingLinkIds: [],
      missingItemPath: '',
    };
  }

  if (params.skipClientFields) {
    return { success: true };
  }

  const requiredFields = validateRequiredTemplateFields(
    params.questionnaire,
    params.response,
  );
  if (!requiredFields.success) return requiredFields;

  if (params.validateTreatmentPlanDiagnosis) {
    const diagnosisValidation = validateTreatmentPlanDiagnosisCodesForPaymentType(
      params.response,
      params.episodePaymentType,
    );
    if (!diagnosisValidation.success) return diagnosisValidation;

    // Assigned/expiration date validation — only when the template actually has the
    // expiration item, so in-flight documents created from older template versions
    // (with no expiration field to fill) can still be signed.
    if (findQuestionnaireItemByLinkIdSuffix(params.questionnaire.item, '-expiration-date')) {
      const dateValidation = validateTreatmentPlanDates(params.response.item);
      if (!dateValidation.valid) {
        return {
          success: false,
          message: dateValidation.errors.join(' '),
          missingLinkIds: [],
          missingItemPath: '',
        };
      }
    }
  }

  return { success: true };
}

/** True when the save action finalizes provider work and should enforce required fields. */
export function chartDocumentSaveActionRequiresFieldValidation(action: string): boolean {
  return action !== 'save';
}
